import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { isMobile } from "react-device-detect";
import { useTranslation } from "react-i18next";

import moment from "moment";
import { numberCommaString } from "helpers";

import { ACTCODE_COMMENT_CONTENTS, ACTCODE_TITLE } from "constant/log";
import { COMMENT_CONTENTS, COMMENT_NICKNAME, COMMENT_USER_ID, TITLE } from "constant/search";
import { JAVASCRIPT_VOID } from "constant/config";

import PrintDate from "components/boards/common/PrintDate";
import Highlight from "components/Highlight";
import Nickname from "components/contents/Nickname";
import { ProfileImage2 } from "components/ImageHandle";
import { POST_PROFILE } from "constant/position.path";
import PostLink from "components/boards/post/PostLink";
import PostHomePin from "components/boards/post/PostHomePin";
import NickNameBadge from "components/boards/post/feed/NickNameBadge";
import { vodInflowLog, getOneStoryPlayerLink } from "helpers";

import { useDispatch, useSelector } from "react-redux";

const VodInfo = ({ vod, inflowPath, authority, location = 'vods' }) => {
	const { t } = useTranslation();

	const dispatch = useDispatch();

	const { open } = useSelector(({ homeIctMenuReducer }) => ({
		open: homeIctMenuReducer.open,
	}));

	// vod 정보 조회
	const { ucc = {}, count = null, comment = null, copyright = null, reserve = null, like = null, vodManager = null } = vod;
	const { auto_delete_remain_hours = null, cate = {} } = ucc;
    
	const vodCounts = useMemo(() =>{
		if(!count){
			return {
				like_cnt : 0,
				read_cnt : 0,
				comment_cnt : 0,
				vod_read_cnt: 0,
			}
		}

		return count;
	}, [count]);

	// 다시 보기 자동 삭제 까지 남은 시간
	const remainHours = useMemo(() => {
		if (!auto_delete_remain_hours) {
			return null;
		}

		return auto_delete_remain_hours < 24
			? auto_delete_remain_hours + 1 + t("시간")
			: Math.ceil(auto_delete_remain_hours / 24) + t("일");
	}, [t, auto_delete_remain_hours]);

	// 영상 게시 날짜 텍스트
	const regDateText = useMemo(() => {
		if ((reserve && !reserve.auto_delete_date) || (!reserve && !vod.reg_date)) {
			return null;
		}

		return reserve
			? reserve.auto_delete_date : vod.reg_date;
	}, [reserve, t, vod.reg_date]);

	// 다시보기 내 스토리 버튼 - 단일 캐치 스토리 플레이어
    const handleStoryVodInflowLog = (vod) => {
		let appendData = {};
		appendData['story_no'] = vod.ucc.story_idx;
		
		if(isMobile){
			inflowPath[3] = "btn";
		} else {
			inflowPath[4] = "btn";
		}

		vodInflowLog(vod.user_id, vod.title_no, inflowPath, appendData);
		if(isMobile){
			window.location.href = getOneStoryPlayerLink(vod.ucc.story_idx);
		} else {
			window.open(getOneStoryPlayerLink(vod.ucc.story_idx));
		}
	};

	// 게시글 좋아요 갯수
	const likeCount = parseInt(vodCounts.like_cnt) + (like ? 1 : 0);
    
    // 실제 업로더 (업로드 매니저 or 원저작자)
    const vodUploder = vodManager ? vodManager : copyright;

	// 다시보기 자동 삭제 안내 말풍선 레이어 표시 여부
	const [isVodDeleteNotiOpen, setIsVodDeleteNotiOpen] = useState(false);

	// PC, 물음표에 마우스 오버/아웃 시 말풍선 표시 설정
	const handleOnMouseOverNoti = () => {
		setIsVodDeleteNotiOpen(true);
	}
	const handleOnMouseOutNoti = () => {
		setIsVodDeleteNotiOpen(false);
	}

	// 모바일, 말풍선 표시 중 말풍선 외부 영역 터치 시
	const vodDeleteNotiRef = useRef(null);
	useEffect(() => {
		if (!isVodDeleteNotiOpen || !isMobile) return;

		function handleOutSideClick(e) {
			if (vodDeleteNotiRef?.current?.contains(e.target)) return; // 말풍선 내부 클릭시 아무것도 하지 않는다.

			e.stopPropagation();
			e.preventDefault();

			setIsVodDeleteNotiOpen(false);
		}

		document.addEventListener("click", handleOutSideClick, true);
		return () => document.removeEventListener("click", handleOutSideClick, true);
	}, [isVodDeleteNotiOpen, vodDeleteNotiRef, isMobile]);

	return (
		<Fragment>
			<div className="vod-info">
				{isMobile && (
					<Fragment>
						{/* 캐치스토리 버튼 노출 */}
						{vod.ucc && vod.ucc.story_idx != null && <button type="button" className="story_view" onClick={() => {handleStoryVodInflowLog(vod);}}>{t("스토리 보기")}</button>}

						{/* 제목 영역 */}
						<PostLink post={vod} className="title" inflowPaths={inflowPath}>{vod.title_name}</PostLink>

						{/* 영상 정보 영역 */}
						<div className="info">
							{/**뱃지 영역 */}
							{vodUploder && vodUploder.user_id && vodUploder.user_nick && vod.badge && (
								<NickNameBadge badge={vod.badge} />
							)}
							{/* 영상 등록한 닉네임 노출 */}
							{vodUploder && vodUploder.user_nick && (
								<Nickname item={vodUploder} top={25} right="auto">
									<div className="nick" >
										<p>{vodUploder.user_nick}</p>
									</div>
								</Nickname>
							)}


							{/* 예약 등록된 영상이 아닐 경우 각종 정보 노출 */}
							{!reserve && (
								<Fragment>
									<span className="views">{numberCommaString(vodCounts.read_cnt)}</span>
									{vod.reg_date && <PrintDate tag="span" attributes={{ className: "date" }} target={vod.reg_date} />}
								</Fragment>
							)}

							{/* 예약 등록된 영상 */}
							{reserve && (
								<p className="schedule">
									{<PrintDate tag="em" target={reserve.auto_delete_date} reserve={!!reserve} /> }
								</p>
							)}

							{/* 다시 보기 자동 삭제까지 남은 시간 노출 */}
							{remainHours &&
							<div className="del_msg_wrap">
								<button type="button" onClick={() => setIsVodDeleteNotiOpen((prev) => !prev)}>
									{remainHours} 후 자동 삭제
									<em>?</em>
								</button>
								{isVodDeleteNotiOpen &&
								<div className="tooltip" ref={vodDeleteNotiRef}>
									<ul>
										<li>다시보기 조회수에 LIVE 참여 수가 반영되어 있습니다.</li>
										<li>조회수 50회 이상은 영구 보관으로 자동 전환됩니다.</li>
									</ul>
									<p>현재 조회수 : {vodCounts.vod_read_cnt}</p>
									<button type="button" className="close" onClick={() => setIsVodDeleteNotiOpen(false)}>레이어 닫기</button>
								</div>}
							</div>}
						</div>

					</Fragment>
				)}
				{!isMobile && (
					<Fragment>
						{
							location === 'home' ? (
								<>
									<div className="info">
										{vodUploder && vodUploder.user_id && vodUploder.user_nick && (
												<div className="nick"
													onClick={(event) => {
														event.stopPropagation();

														if (open ) {
															dispatch({type: 'ICT_CLOSE_MODAL'});
															dispatch({type: 'LATER_CLOSE_MODAL'});
															return;
														}

														if (!open && event && event.target) {
															const { top, left, bottom } = event.target.getBoundingClientRect();
															let newTop = top + window.scrollY;
															let newLeft = left + window.scrollX;

															dispatch({
																type: "ICT_OPEN_MODAL",
																payload: {
																	item: vodUploder,
																	top: (newTop + 25),
																	left: newLeft,
																	children: event.target,
																},
															});
														}
													}}
												>
													{vodUploder.user_nick}
												</div>
										)}
										{vodUploder && vodUploder.user_id && vodUploder.user_nick && vod.badge && (
											<NickNameBadge badge={vod.badge} />
										)}
									</div>
									<p className="title" >
										<PostLink post={vod} inflowPaths={inflowPath}>
											<Highlight type={TITLE} __html={vod.title_name} />
										</PostLink>
									</p>
									<div className="info">
										{/* 조회 수 노출 */}
										<span className="views">
											<em />
											{`${numberCommaString(vodCounts.read_cnt)}`}
										</span>
										{/* 추천 수 노출 */}
										<span className="up">
											<em />
											{numberCommaString(likeCount)}
										</span>

										{/* 댓글 수 노출 */}
										<span className="coment">
											<em />
											{vodCounts.comment_cnt}
										</span>

										{/* 영상 게시 날짜 노출  */}
										{regDateText && <PrintDate tag="span" attributes={{ className: "date" }} target={regDateText} reserve={!!reserve} />}

										{/* 카테고리명 노출 */}
										{cate.cate_name && <em className="cate">{cate.cate_name}</em>}

										{/* 캐치스토리 버튼 노출 */}
										{vod.ucc.story_idx != null && <button type="button" className="story_view" onClick={() => {handleStoryVodInflowLog(vod);}}>{t("스토리 보기")}</button>}
									</div>
								</>
							) : (
								<>
									<p className="title">
										<PostLink post={vod} inflowPaths={inflowPath}>
											<Highlight type={TITLE} __html={vod.title_name} />
										</PostLink>
									</p>
									<div className="info">
										{/**뱃지 영역 */}
										{vodUploder && vodUploder.user_id && vodUploder.user_nick && vod.badge && (
											<NickNameBadge badge={vod.badge} />
										)}
										{/* 영상 등록한 닉네임 노출*/}
										{vodUploder && vodUploder.user_id && vodUploder.user_nick && (
											<Nickname item={vodUploder} right="auto">
												<a href={JAVASCRIPT_VOID} className="nick">
													{vodUploder.user_nick || vodUploder.user_id}
												</a>
											</Nickname>
										)}

										{/* 조회 수 노출 */}
										<span className="views">
											<em />
											{`${numberCommaString(vodCounts.read_cnt)}`}
										</span>

										{/* 추천 수 노출 */}
										<span className="up">
											<em />
											{numberCommaString(likeCount)}
										</span>

										{/* 댓글 수 노출 */}
										<span className="coment">
											<em />
											{vodCounts.comment_cnt}
										</span>

										{/* 영상 게시 날짜 노출  */}

										{regDateText && <PrintDate tag="span" attributes={{ className: "date" }} target={regDateText} reserve={!!reserve} />}

										{/* 다시 보기 자동 삭제까지 남은 시간 노출 */}
										{remainHours &&
										<div className="del_msg_wrap">
											<button type="button" onMouseOver={handleOnMouseOverNoti} onMouseOut={handleOnMouseOutNoti}>
												{remainHours} 후 자동 삭제
												<em>?</em>
											</button>
											{isVodDeleteNotiOpen &&
											<div className="tooltip">
												<ul>
													<li>다시보기 조회수에 LIVE 참여 수가 반영되어 있습니다.</li>
													<li>조회수 50회 이상은 영구 보관으로 자동 전환됩니다.</li>
												</ul>
												<p>현재 조회수 : {vodCounts.vod_read_cnt}</p>
											</div>}
										</div>}

										{/* 카테고리명 노출 */}
										{cate.cate_name && <em className="cate">{cate.cate_name}</em>}

										{/* 캐치스토리 버튼 노출 */}
										{vod.ucc.story_idx != null &&
											<button type="button" className="story_view" onClick={() => {
												handleStoryVodInflowLog(vod);
											}}>{t("스토리 보기")}</button>}
									</div>
								</>

							)
						}


						{/* 댓글 영역 (댓글 검색 시 노출) */}
						{comment && (
							<div className="cmmt_box">
								<ProfileImage2 userId={comment.user_id} className="thum" src={comment.profile_image}
											   position={POST_PROFILE} />
								<div className="innr">
									<div>
										<PostLink post={vod} className="cmmt_text" inflowPaths={inflowPath}>
											<span>
												<Highlight type={COMMENT_NICKNAME} __html={comment.user_nick} />
												<em>
													<Highlight type={COMMENT_USER_ID} __html={`(${comment.user_id})`} />
												</em>
											</span>
											<Highlight type={COMMENT_CONTENTS} __html={comment.content} />
										</PostLink>
									</div>
								</div>
							</div>
						)}
					</Fragment>
				)}
			</div>

			{/* 핀 고정 / 고정 해제 */}
			{/*{!isMobile && <PostHomePin post={vod} authority={authority} />}*/}
		</Fragment>
	);
};

export default VodInfo;
